import type { Location } from 'react-router-dom';
import {
  getMembershipRedirectPath,
  type MembershipRedirectData,
} from '../../utils/membershipRedirect';

/** After login: /auth/auth?redirect_page=didar-setting&id=... */
export const DIDAR_SETTING_REDIRECT_PAGE_PARAM = 'redirect_page';

export const DIDAR_SETTING_REDIRECT_PAGE_VALUE = 'didar-setting';

/** Form id in URL: /didar-setting?id=... */
export const DIDAR_SETTING_FORM_ID_PARAM = 'id';

export const DIDAR_CUSTOMER_FORM_URL_PREFIX = 'https://app.didar.me/customer/form/';

export const DIDAR_SETTING_PUBLIC_PATH = '/didar-setting';

const AIDA_SETTINGS_TAB = '3';
/** Connections → اطرافیان آیدا (Marketplace) */
const AIDA_SETTINGS_MARKETPLACE_SUBTAB = '0';
const AIDA_SETTINGS_CONNECTIONS_SUBTAB = '2';

/** Show warning snackbar on marketplace when Didar is not in user's integrations */
export const DIDAR_SETTING_MISSING_INTEGRATION_PARAM = 'didar_missing';

export const DIDAR_SETTING_MISSING_INTEGRATION_VALUE = '1';

export const DIDAR_SETTING_SNACKBAR_OPTIONS = {
  variant: 'warning' as const,
  autoHideDuration: 5000,
  style: { padding: '10px', marginTop: '20px' },
};

export const isDidarSettingRedirectPage = (value: string | null): boolean =>
  value === DIDAR_SETTING_REDIRECT_PAGE_VALUE;

export const isDidarSettingPath = (pathname: string): boolean =>
  pathname === DIDAR_SETTING_PUBLIC_PATH;

export const buildDidarFormUrlFromId = (formId: string): string =>
  `${DIDAR_CUSTOMER_FORM_URL_PREFIX}${formId.trim()}`;

/** Aida Settings → Connections (tab 3) → Didar advanced settings (subtab 2) */
export function buildDidarSettingTargetPath(search?: string): string {
  const params = new URLSearchParams({
    tab: AIDA_SETTINGS_TAB,
    subtab: AIDA_SETTINGS_CONNECTIONS_SUBTAB,
  });

  const incoming = new URLSearchParams(
    search?.startsWith('?') ? search.slice(1) : search ?? '',
  );
  const formId = incoming.get(DIDAR_SETTING_FORM_ID_PARAM)?.trim();
  if (formId) {
    params.set(DIDAR_SETTING_FORM_ID_PARAM, formId);
  }

  return `/aida-settings?${params.toString()}`;
}

/** Aida Settings → Connections (tab 3) → اطرافیان آیدا (subtab 0) */
export function buildDidarMarketplaceTargetPath(options?: {
  showMissingSnackbar?: boolean;
}): string {
  const params = new URLSearchParams({
    tab: AIDA_SETTINGS_TAB,
    subtab: AIDA_SETTINGS_MARKETPLACE_SUBTAB,
  });

  if (options?.showMissingSnackbar) {
    params.set(
      DIDAR_SETTING_MISSING_INTEGRATION_PARAM,
      DIDAR_SETTING_MISSING_INTEGRATION_VALUE,
    );
  }

  return `/aida-settings?${params.toString()}`;
}

export const isDidarMissingIntegrationFlag = (
  searchParams: URLSearchParams,
): boolean =>
  searchParams.get(DIDAR_SETTING_MISSING_INTEGRATION_PARAM) ===
  DIDAR_SETTING_MISSING_INTEGRATION_VALUE;

export const AIDA_SETTINGS_PATH = '/aida-settings';

/** Didar deep-link query params to strip once when Didar is not active */
export const didarSettingSearchParamsNeedCleanup = (
  searchParams: URLSearchParams,
): boolean => {
  if (searchParams.get(DIDAR_SETTING_FORM_ID_PARAM)?.trim()) {
    return true;
  }
  if (searchParams.get('subtab') === AIDA_SETTINGS_CONNECTIONS_SUBTAB) {
    return true;
  }
  if (isDidarMissingIntegrationFlag(searchParams)) {
    return true;
  }
  return false;
};

/** Remove Didar deep-link params; keep parent Aida Settings tab (e.g. tab=3) when present */
export const stripDidarDeepLinkSearchParams = (
  searchParams: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  next.delete(DIDAR_SETTING_FORM_ID_PARAM);
  next.delete('subtab');
  next.delete(DIDAR_SETTING_MISSING_INTEGRATION_PARAM);
  return next;
};

export const buildAidaSettingsPathAfterDidarCleanup = (
  searchParams: URLSearchParams,
): string => {
  const next = stripDidarDeepLinkSearchParams(searchParams);
  const qs = next.toString();
  return qs ? `${AIDA_SETTINGS_PATH}?${qs}` : AIDA_SETTINGS_PATH;
};

/** Remove routing form id only; keep tab/subtab so the current view stays active */
export const buildAidaSettingsPathWithoutFormId = (
  searchParams: URLSearchParams,
): string => {
  const next = new URLSearchParams(searchParams);
  next.delete(DIDAR_SETTING_FORM_ID_PARAM);
  const qs = next.toString();
  return qs ? `${AIDA_SETTINGS_PATH}?${qs}` : AIDA_SETTINGS_PATH;
};

/** @deprecated Use buildDidarSettingTargetPath() */
export const DIDAR_SETTING_TARGET_PATH = buildDidarSettingTargetPath();

export function getPostLoginRedirectPath(
  searchParams: URLSearchParams,
  location: Location,
  membership?: MembershipRedirectData | null,
): string {
  if (isDidarSettingRedirectPage(searchParams.get(DIDAR_SETTING_REDIRECT_PAGE_PARAM))) {
    return buildDidarSettingTargetPath(`?${searchParams.toString()}`);
  }

  const from = (location.state as { from?: Location } | null)?.from;
  if (from && isDidarSettingPath(from.pathname)) {
    return buildDidarSettingTargetPath(from.search);
  }

  if (from?.pathname) {
    return `${from.pathname}${from.search ?? ''}`;
  }

  return getMembershipRedirectPath(membership);
}

export function getAuthRedirectSearch(pathname: string, search: string): string {
  if (!isDidarSettingPath(pathname)) {
    return search;
  }

  const incoming = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const params = new URLSearchParams();
  params.set(DIDAR_SETTING_REDIRECT_PAGE_PARAM, DIDAR_SETTING_REDIRECT_PAGE_VALUE);

  const formId = incoming.get(DIDAR_SETTING_FORM_ID_PARAM)?.trim();
  if (formId) {
    params.set(DIDAR_SETTING_FORM_ID_PARAM, formId);
  }

  return `?${params.toString()}`;
}
